import { useCallback, useEffect, useState } from 'react';
import { billingApi, discountsApi, productsApi } from '@/api';
import type { PosCartItem, PosCartState } from '@/types/billing';
import type { ProductResponse } from '@/types/product';
import type { PosProductScanResponse } from '@/types/storeProduct';
import type { PosModifierOption } from '@/utils/posModifiers';
import {
  calcLineTotal,
  cartTotals,
  productToCartItem,
  scanToCartItem,
  serializeCart,
} from '@/utils/posCart';
import { showApiError } from '@/utils/apiError';

import { notify } from '@/core/toast/notify';
export const emptyCart = (): PosCartState => ({
  items: [],
  discountAmount: 0,
  gstInclusive: true,
});

export function usePosCart(storeId: number | undefined) {
  const [cart, setCart] = useState<PosCartState>(emptyCart);
  const [products, setProducts] = useState<ProductResponse[]>([]);
  const [search, setSearch] = useState('');

  useEffect(() => {
    setCart(emptyCart());
    setProducts([]);
    setSearch('');
  }, [storeId]);

  const totals = cartTotals(cart);

  const addScan = useCallback((scan: PosProductScanResponse) => {
    setCart((prev) => {
      const item = scanToCartItem(scan, 1, prev.gstInclusive);
      const existing = prev.items.find((i) => i.productId === item.productId);
      if (existing) {
        const qty = existing.quantity + 1;
        const lineTotal = calcLineTotal(
          qty,
          existing.unitPrice,
          existing.taxPercentage,
          existing.discountAmount,
          prev.gstInclusive,
        );
        return {
          ...prev,
          items: prev.items.map((i) =>
            i.productId === item.productId ? { ...i, quantity: qty, lineTotal } : i,
          ),
        };
      }
      return { ...prev, items: [...prev.items, item] };
    });
  }, []);

  const addProduct = useCallback((product: ProductResponse, qty = 1) => {
    const quantity = qty > 0 ? qty : 1;
    setCart((prev) => {
      const existing = prev.items.find(
        (i) => i.productId === product.id && !i.modifierLabels,
      );
      if (existing) {
        const nextQty = existing.quantity + quantity;
        const lineTotal = calcLineTotal(
          nextQty,
          existing.unitPrice,
          existing.taxPercentage,
          existing.discountAmount,
          prev.gstInclusive,
        );
        return {
          ...prev,
          items: prev.items.map((i) =>
            i.productId === product.id && !i.modifierLabels ? { ...i, quantity: nextQty, lineTotal } : i,
          ),
        };
      }
      return {
        ...prev,
        items: [...prev.items, productToCartItem(product, quantity, prev.gstInclusive)],
      };
    });
    setSearch('');
    setProducts([]);
  }, []);

  const addProductWithModifiers = useCallback(
    (product: ProductResponse, modifiers: PosModifierOption[], extraTotal: number, qty = 1) => {
      const quantity = qty > 0 ? qty : 1;
      setCart((prev) => {
        const basePrice = Number(product.sellingPrice) + extraTotal / quantity;
        const taxPercentage = Number(product.taxPercentage ?? 0);
        const item: PosCartItem = {
          ...productToCartItem(product, quantity, prev.gstInclusive),
          unitPrice: basePrice,
          modifierLabels: modifiers.map((m) => m.name).join(', '),
          lineModifiersJson: JSON.stringify(modifiers.map((m) => ({ name: m.name, price: m.price }))),
          lineTotal: calcLineTotal(quantity, basePrice, taxPercentage, 0, prev.gstInclusive),
        };
        return { ...prev, items: [...prev.items, item] };
      });
      setSearch('');
      setProducts([]);
    },
    [],
  );

  const searchProducts = useCallback(
    async (q: string) => {
      if (!q.trim()) {
        setProducts([]);
        return;
      }
      if (storeId) {
        try {
          const scan = await billingApi.scanProduct(storeId, q.trim());
          addScan(scan);
          setSearch('');
          setProducts([]);
          return;
        } catch {
          /* catalog fallback */
        }
      }
      try {
        const page = await productsApi.list({ search: q, size: 12, activeOnly: true });
        setProducts(page.content);
      } catch {
        setProducts([]);
      }
    },
    [storeId, addScan],
  );

  const updateLineDiscount = useCallback((index: number, amount: number) => {
    setCart((prev) => ({
      ...prev,
      items: prev.items.map((item, i) => {
        if (i !== index) return item;
        const discountAmount = Math.max(0, amount);
        const lineTotal = calcLineTotal(
          item.quantity,
          item.unitPrice,
          item.taxPercentage,
          discountAmount,
          prev.gstInclusive,
        );
        return { ...item, discountAmount, lineTotal };
      }),
    }));
    notify.success('Line discount updated');
  }, []);

  const updateQty = useCallback((index: number, qty: number) => {
    if (qty <= 0) return;
    setCart((prev) => ({
      ...prev,
      items: prev.items.map((item, i) => {
        if (i !== index) return item;
        const lineTotal = calcLineTotal(
          qty,
          item.unitPrice,
          item.taxPercentage,
          item.discountAmount,
          prev.gstInclusive,
        );
        return { ...item, quantity: qty, lineTotal };
      }),
    }));
  }, []);

  const removeItem = useCallback((index: number) => {
    setCart((prev) => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
  }, []);

  const clearCart = useCallback(() => {
    setCart(emptyCart());
    notify.success('Cart cleared');
  }, []);

  const setCustomerId = useCallback((customerId: number | undefined) => {
    setCart((prev) => ({ ...prev, customerId }));
  }, []);

  const applyBillDiscount = useCallback((amount: number) => {
    setCart((c) => ({ ...c, discountAmount: Math.max(0, amount) }));
    notify.success('Discount applied');
  }, []);

  const applyCoupon = useCallback(
    async (code: string) => {
      const coupon = await discountsApi.validateCoupon(code);
      let discount = 0;
      if (coupon.discountType === 'PERCENTAGE') {
        discount = (totals.itemsTotal * Number(coupon.value)) / 100;
      } else {
        discount = Number(coupon.value);
      }
      discount = Math.min(discount, totals.itemsTotal);
      setCart((c) => ({
        ...c,
        couponCode: code,
        discountAmount: Number(discount.toFixed(2)),
      }));
      notify.success(`Coupon ${code} applied`);
      return Number(discount.toFixed(2));
    },
    [totals.itemsTotal],
  );

  const holdBill = useCallback(
    async (customerId: number | undefined) => {
      if (!storeId || cart.items.length === 0) return false;
      try {
        await billingApi.holdBill({
          storeId,
          counterId: (() => {
            const raw = sessionStorage.getItem('fattahone_selected_counter_id');
            const n = raw ? Number(raw) : undefined;
            return n && !Number.isNaN(n) ? n : undefined;
          })(),
          customerId,
          billData: serializeCart(cart),
          holdReference: `HOLD-${Date.now()}`,
        });
        setCart(emptyCart());
        notify.success('Bill held');
        return true;
      } catch (err) {
        showApiError(err, 'Hold failed');
        return false;
      }
    },
    [storeId, cart],
  );

  const loadCart = useCallback((next: PosCartState) => {
    setCart(next);
  }, []);

  return {
    cart,
    setCart,
    loadCart,
    products,
    search,
    setSearch,
    searchProducts,
    addProduct,
    addProductWithModifiers,
    addScan,
    updateQty,
    updateLineDiscount,
    removeItem,
    clearCart,
    totals,
    setCustomerId,
    applyBillDiscount,
    applyCoupon,
    holdBill,
  };
}
